export type SurfaceId =
  | "intake"
  | "grader"
  | "forge"
  | "planner"
  | "merge"
  | "runtime"
  | "export"
  | "deck"
  | "pipeline"
  | "agents"
  | "recovery"
  | "portfolio";

export type PipelineStage =
  | "intake"
  | "decomposition"
  | "architecture"
  | "codeBuild"
  | "testRepair"
  | "packaging"
  | "handoff";

export type AgentRole = "bob" | "felix" | "lisa" | "executor";

export type DirectivePriority = "low" | "medium" | "high" | "critical";

export type StackPreference =
  | "react-vite"
  | "nextjs"
  | "cloudflare-workers"
  | "static-html"
  | "unspecified";

export type TargetOutput =
  | "web-app"
  | "worker-api"
  | "component-library"
  | "docs-site"
  | "handoff-pack";

export interface BuildIntake {
  id: string;
  projectName: string;
  projectType: string;
  targetOutput: TargetOutput;
  stackPreference: StackPreference;
  sourceNotes: string;
  constraints: string;
  noApiPaywall: true;
  createdAt: string;
}

export interface BuildDirective {
  id: string;
  intakeId?: string;
  name: string;
  objective: string;
  stage: PipelineStage;
  progress: number;
  priority: DirectivePriority;
  assignedRole: AgentRole;
  paused: boolean;
  createdAt: string;
  updatedAt: string;
}

export type AgentStatus = "idle" | "active" | "blocked" | "offline";

export interface AgentCard {
  id: string;
  role: AgentRole;
  displayName: string;
  status: AgentStatus;
  currentDirectiveId?: string;
  taskSummary: string;
  utilization: number;
}

export interface RepairAction {
  id: string;
  label: string;
  description: string;
}

export type FailureStatus = "open" | "repairing" | "resolved" | "escalated";

export interface FailureCard {
  id: string;
  directiveId?: string;
  title: string;
  description: string;
  severity: "low" | "medium" | "high" | "critical";
  stage: PipelineStage;
  repairActions: RepairAction[];
  status: FailureStatus;
  createdAt: string;
  updatedAt: string;
}

export interface RepoGraderInput {
  pathOrPaste: string;
}

export interface RepoGraderResult {
  id: string;
  scannedAt: string;
  checklist: ChecklistItem[];
  riskFlags: RiskFlag[];
  salvageScore: number;
  suggestedBuildPath: string;
  summary: string;
}

export interface ChecklistItem {
  id: string;
  label: string;
  passed: boolean;
  detail: string;
}

export interface RiskFlag {
  id: string;
  severity: "low" | "medium" | "high";
  category: string;
  message: string;
}

export type PromptFormat = "cursor" | "claude-code" | "lovable" | "local-build";

export interface PromptForgeInput {
  intent: string;
  format: PromptFormat;
  intakeId?: string;
}

export interface PromptForgeResult {
  id: string;
  format: PromptFormat;
  markdown: string;
  createdAt: string;
}

export interface ArtifactPlan {
  id: string;
  intakeId?: string;
  fileTree: FileTreeNode[];
  requiredFiles: string[];
  missingFiles: string[];
  commands: string[];
  verification: string[];
  deployTarget: string;
  createdAt: string;
}

export interface FileTreeNode {
  path: string;
  kind: "file" | "dir";
  note?: string;
}

export interface MergeCandidate {
  id: "A" | "B";
  label: string;
  path: string;
}

export interface MergePlan {
  id: string;
  candidateA: MergeCandidate;
  candidateB: MergeCandidate;
  base: "A" | "B" | "composite";
  salvage: "A" | "B" | "both" | "neither";
  featureTransfer: string[];
  conflictFlags: string[];
  riskFlags: string[];
  patchSteps: string[];
  createdAt: string;
}

export interface AdapterStatus {
  id: string;
  name: string;
  enabled: boolean;
  available: boolean;
  mode: "local-deterministic" | "optional-adapter";
  description: string;
}

export interface SovereignRuntimeStatus {
  offlineCapable: boolean;
  apiKeysRequired: boolean;
  storageMode: "localStorage";
  exportMode: "browser-download";
  adapters: AdapterStatus[];
  noPaywallLocked: true;
  checkedAt: string;
}

export interface BobState {
  intakes: BuildIntake[];
  graderResults: RepoGraderResult[];
  prompts: PromptForgeResult[];
  artifactPlans: ArtifactPlan[];
  mergePlans: MergePlan[];
  directives: BuildDirective[];
  agents: AgentCard[];
  failures: FailureCard[];
  activeSurface: SurfaceId;
  presentationMode: boolean;
  sovereign: SovereignRuntimeStatus;
}

export const BOB_VERSION = "2.0.0";
export const BOB_MARKER = {
  title: "BOB Sovereign Builder v2.0",
  runtime: "Local-first / No API paywall",
  target: "bob.arkmpyre.com",
} as const;
